import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Subscription, firstValueFrom } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import * as signalR from '@microsoft/signalr';
import { AuthService } from '@/app/auth/shared/services/auth.service';
import { NotificationService } from './notification.service';
import { AppConfigService } from './app-config.service';
import { MyNotificationsViewModel } from '@/app/proxy/proxies';
import { SKIP_API_TOAST } from '../interceptors/api-toast.interceptor';

interface NegotiateResponse {
    url: string;
    accessToken: string;
}

/** Injects ngsw-bypass on every SignalR HTTP request (Azure /client/negotiate uses native fetch, not HttpClient). */
class NgswBypassSignalRHttpClient extends signalR.DefaultHttpClient {
    constructor(logger: signalR.ILogger) {
        super(logger);
    }

    send(request: signalR.HttpRequest): Promise<signalR.HttpResponse> {
        request.headers = { ...request.headers, 'ngsw-bypass': 'true' };
        return super.send(request);
    }
}

@Injectable({
    providedIn: 'root',
})
export class NotificationSignalrService implements OnDestroy {
    private connection?: signalR.HubConnection;
    private authSubscription?: Subscription;
    private bootstrapped = false;
    private connecting = false;

    private static readonly NEGOTIATE_MAX_ATTEMPTS = 3;
    private static readonly RETRYABLE_STATUSES = new Set([0, 502, 503, 504]);
    private static readonly NGSW_BYPASS_HEADERS = { 'ngsw-bypass': 'true' } as const;

    // Dedicated stream for SignalR notifications (for test page)
    public signalrNotifications$ = new BehaviorSubject<MyNotificationsViewModel[]>([]);

    constructor(
        private httpClient: HttpClient,
        private authService: AuthService,
        private notificationService: NotificationService,
        private appConfigService: AppConfigService
    ) {
        // No longer auto-connect in constructor to avoid race conditions with app config loading
    }

    ngOnDestroy(): void {
        this.disconnect();
        if (this.authSubscription) {
            this.authSubscription.unsubscribe();
        }
    }

    /**
     * Bootstrap the SignalR connection.
     * Should be called once from FeaturesComponent (for returning users with valid tokens)
     * or from LoginComponent (after successful login).
     * Idempotent - safe to call multiple times.
     */
    public async bootstrap(): Promise<void> {
        if (this.bootstrapped) {
            return;
        }
        this.bootstrapped = true;

        await this.appConfigService.initializeApp();

        // Connect immediately if authenticated
        if (this.authService.isAuthenticated()) {
            await this.connect();
        }

        // Listen for auth state changes
        this.authSubscription = this.authService.userSession$
            .pipe(
                distinctUntilChanged((prev, curr) => {
                    const prevId = prev?.id || null;
                    const currId = curr?.id || null;
                    return prevId === currId;
                })
            )
            .subscribe((_userSession) => {
                const isAuthenticatedNow = this.authService.isAuthenticated();
                const hasActiveConnection = !!this.connection;

                if (isAuthenticatedNow && !hasActiveConnection && !this.connecting) {
                    void this.connect();
                } else if (!isAuthenticatedNow && hasActiveConnection) {
                    this.disconnect();
                }
            });
    }

    private async connect(): Promise<void> {
        if (this.connecting) {
            console.log('[SignalR] Connection already in progress');
            return;
        }

        // Guard: must be authenticated
        if (!this.authService.isAuthenticated()) {
            console.log('[SignalR] Cannot connect: not authenticated');
            return;
        }

        // Already connected
        if (this.connection) {
            console.log('[SignalR] Already connected');
            return;
        }

        this.connecting = true;
        try {
            // Ensure app configuration and authenticated configuration are loaded
            await this.appConfigService.initializeApp();
            await this.appConfigService.initializeAuthenticatedConfigurations();

            // Step 1: Always fetch initial notification history (even if SignalR can't connect)
            console.log('[SignalR] Fetching initial notification history...');
            this.notificationService.fetchNotifications().subscribe({
                next: (notifications) => {
                    this.notificationService.forceEmitNewNotifications(notifications);
                    console.log(`[SignalR] Loaded ${notifications.length} notifications from history`);
                },
                error: (error) => {
                    console.error('[SignalR] Error fetching initial notifications:', error);
                },
            });

            // Guard: check if SignalR negotiate URL is configured
            const negotiateUrl = this.appConfigService.signalrNegotiateUrl;
            if (!negotiateUrl) {
                console.log('[SignalR] Cannot connect: Core.Notification.Negotiate.URL config key not found');
                return;
            }

            if (this.connection) {
                return;
            }

            // Step 2: Negotiate with the Function to get SignalR connection info
            console.log(`[SignalR] Negotiating with ${negotiateUrl}...`);
            const negotiateResponse = await this.negotiateWithRetry(negotiateUrl);

            if (!negotiateResponse.url || !negotiateResponse.accessToken) {
                console.error('[SignalR] Invalid negotiate response:', negotiateResponse);
                return;
            }

            console.log(`[SignalR] Negotiated successfully. Connecting to ${negotiateResponse.url}...`);

            // Step 3: Build SignalR connection (Azure /client/negotiate bypasses SW via headers + custom HttpClient)
            this.connection = new signalR.HubConnectionBuilder()
                .withUrl(negotiateResponse.url, {
                    accessTokenFactory: () => negotiateResponse.accessToken,
                    headers: { ...NotificationSignalrService.NGSW_BYPASS_HEADERS },
                    httpClient: new NgswBypassSignalRHttpClient(signalR.NullLogger.instance),
                })
                .withAutomaticReconnect()
                .configureLogging(signalR.LogLevel.Warning)
                .build();

            // Step 4: Register event handlers
            this.connection.on('ReceiveNotification', (payload: unknown) => {
                console.log('[SignalR] ReceiveNotification:', payload);
                const notification = MyNotificationsViewModel.fromJS(payload);

                // Add to main notification service (for header bell, etc.)
                this.notificationService.addRealtimeNotification(notification);

                // Also add to SignalR-specific stream (for test page)
                const currentSignalrNotifications = this.signalrNotifications$.value;
                const updatedSignalrNotifications = [notification, ...currentSignalrNotifications];
                this.signalrNotifications$.next(updatedSignalrNotifications);
            });

            this.connection.onreconnecting((error) => {
                console.log('[SignalR] Reconnecting...', error?.message);
            });

            this.connection.onreconnected((connectionId) => {
                console.log(`[SignalR] Reconnected. ConnectionId: ${connectionId}`);
                // Re-fetch history to catch any notifications missed while offline
                this.notificationService.fetchNotifications().subscribe({
                    next: (notifications) => {
                        this.notificationService.forceEmitNewNotifications(notifications);
                        console.log(`[SignalR] Re-fetched ${notifications.length} notifications after reconnect`);
                    },
                    error: (error) => {
                        console.error('[SignalR] Error re-fetching notifications after reconnect:', error);
                    },
                });
            });

            this.connection.onclose((error) => {
                console.log('[SignalR] Connection closed', error?.message);
                this.connection = undefined;
            });

            // Step 5: Start the connection
            await this.startConnectionWithRetry(this.connection);
            console.log(`[SignalR] Connected. ConnectionId: ${this.connection.connectionId}`);
        } catch (error) {
            console.error('[SignalR] Connection error:', error);
            this.connection = undefined;
        } finally {
            this.connecting = false;
        }
    }

    /** Retries negotiate on transient failures (504/502/503/network) with exponential backoff. */
    private async negotiateWithRetry(negotiateUrl: string): Promise<NegotiateResponse> {
        for (let attempt = 0; attempt < NotificationSignalrService.NEGOTIATE_MAX_ATTEMPTS; attempt++) {
            try {
                return await firstValueFrom(
                    this.httpClient.post<NegotiateResponse>(negotiateUrl, null, {
                        context: new HttpContext().set(SKIP_API_TOAST, true),
                    })
                );
            } catch (error) {
                const status = this.getErrorStatus(error);
                const isLastAttempt = attempt === NotificationSignalrService.NEGOTIATE_MAX_ATTEMPTS - 1;

                if (!NotificationSignalrService.RETRYABLE_STATUSES.has(status) || isLastAttempt) {
                    throw error;
                }

                const delayMs = Math.min(1000 * Math.pow(2, attempt), 4000);
                console.warn(
                    `[SignalR] Negotiate attempt ${attempt + 1} failed (${status}), retrying in ${delayMs}ms...`
                );
                await this.delay(delayMs);
            }
        }

        throw new Error('[SignalR] Negotiate failed after retries');
    }

    /** Retries Azure /client/negotiate (connection.start) on transient SW/gateway failures. */
    private async startConnectionWithRetry(connection: signalR.HubConnection): Promise<void> {
        for (let attempt = 0; attempt < NotificationSignalrService.NEGOTIATE_MAX_ATTEMPTS; attempt++) {
            try {
                await connection.start();
                return;
            } catch (error) {
                const status = this.getErrorStatus(error);
                const isLastAttempt = attempt === NotificationSignalrService.NEGOTIATE_MAX_ATTEMPTS - 1;

                if (!NotificationSignalrService.RETRYABLE_STATUSES.has(status) || isLastAttempt) {
                    throw error;
                }

                const delayMs = Math.min(1000 * Math.pow(2, attempt), 4000);
                console.warn(
                    `[SignalR] Connection start attempt ${attempt + 1} failed (${status}), retrying in ${delayMs}ms...`
                );
                await this.delay(delayMs);
            }
        }

        throw new Error('[SignalR] Connection start failed after retries');
    }

    private getErrorStatus(error: unknown): number {
        if (error instanceof HttpErrorResponse) {
            return error.status;
        }
        if (error && typeof error === 'object' && 'statusCode' in error) {
            const statusCode = (error as { statusCode: unknown }).statusCode;
            if (typeof statusCode === 'number') {
                return statusCode;
            }
        }
        return 0;
    }

    private delay(ms: number): Promise<void> {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    private async disconnect(): Promise<void> {
        if (this.connection) {
            try {
                await this.connection.stop();
                console.log('[SignalR] Disconnected');
            } catch (error) {
                console.error('[SignalR] Error disconnecting:', error);
            } finally {
                this.connection = undefined;
            }
        }
    }

    /**
     * Get connection status for debugging
     */
    public getConnectionStatus(): {
        isConnected: boolean;
        connectionId: string | null;
        state: string;
    } {
        return {
            isConnected: this.connection?.state === signalR.HubConnectionState.Connected,
            connectionId: this.connection?.connectionId || null,
            state: this.connection?.state?.toString() || 'Disconnected',
        };
    }
}
